import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Badge,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Info,
  ExpandMore,
  Download,
  Delete,
  History,
  Assessment,
  Visibility,
  CloudUpload,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { bulkImportAPI } from '../../../services/leadGenAPI';
import CampaignSelector from '../../../components/CampaignSelector';

const BulkImportJobs = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const progressIntervalRef = useRef(null);

  // State management
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [jobSummary, setJobSummary] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showCampaignAssignment, setShowCampaignAssignment] = useState(false);
  const [selectedJobForAssignment, setSelectedJobForAssignment] = useState(null);
  const [selectedCampaignsForAssignment, setSelectedCampaignsForAssignment] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accountId, setAccountId] = useState(1); // Default account ID

  useEffect(() => {
    loadJobs();
    loadJobSummary();
  }, []);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await bulkImportAPI.getImportJobs();
      setJobs(response.results || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadJobSummary = async () => {
    try {
      const response = await bulkImportAPI.getJobSummary();
      setJobSummary(response);
    } catch (error) {
      console.error('Failed to load job summary:', error);
    }
  };

  const handleJobAction = async (jobId, action) => {
    try {
      switch (action) {
        case 'start':
          await bulkImportAPI.startJobProcessing(jobId);
          break;
        case 'cancel':
          await bulkImportAPI.cancelJob(jobId);
          break;
        case 'retry':
          await bulkImportAPI.retryJob(jobId);
          break;
      }
      loadJobs();
      setSuccess(`Job ${action} successful`);
    } catch (error) {
      setError(`Job action failed: ${error.message}`);
    }
  };

  const assignCampaignsToJob = async (jobId, campaignIds) => {
    try {
      await bulkImportAPI.assignCampaignsToJob(jobId, campaignIds);
      loadJobs();
      setSuccess('Campaigns assigned successfully');
    } catch (error) {
      setError(`Failed to assign campaigns: ${error.message}`);
    }
  };

  const openCampaignAssignment = (job) => {
    setSelectedJobForAssignment(job);
    setSelectedCampaignsForAssignment(job.target_campaign_ids || []);
    setShowCampaignAssignment(true);
  };

  const handleCampaignAssignment = async () => {
    if (!selectedJobForAssignment) return;
    
    try {
      await assignCampaignsToJob(selectedJobForAssignment.id, selectedCampaignsForAssignment);
      setShowCampaignAssignment(false);
      setSelectedJobForAssignment(null);
      setSelectedCampaignsForAssignment([]);
    } catch (error) {
      // Error is already handled in assignCampaignsToJob
    }
  };

  const viewJobDetails = async (job) => {
    setSelectedJob(job);
    try {
      const details = await bulkImportAPI.getImportJob(job.id);
      setJobDetails(details);
      setShowJobDetails(true);
    } catch (error) {
      setError(`Failed to load job details: ${error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'processing': return <CircularProgress size={20} />;
      case 'pending': return <Info color="info" />;
      default: return <Info />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderJobSummary = () => (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Total Jobs
            </Typography>
            <Typography variant="h4" component="div">
              {jobSummary?.total_jobs || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              All time
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Active Jobs
            </Typography>
            <Typography variant="h4" component="div">
              {(jobSummary?.pending_jobs || 0) + (jobSummary?.processing_jobs || 0)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Currently running
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Success Rate
            </Typography>
            <Typography variant="h4" component="div">
              {jobSummary?.success_rate?.toFixed(1) || 0}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Overall success
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">
              Records Processed
            </Typography>
            <Typography variant="h4" component="div">
              {jobSummary?.total_records_processed?.toLocaleString() || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total records
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderJobsTable = () => (
    <Paper>
      <TableContainer>
        <Table>
                      <TableHead>
              <TableRow>
                <TableCell>Job ID & Name</TableCell>
                <TableCell>Import Type</TableCell>
                <TableCell>List Type</TableCell>
                <TableCell>Campaigns</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Records</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
                          {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No import jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
            ) : (
              jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        #{job.id} - {job.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.import_type}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.import_type?.replace('suppression_', '').replace('_', ' ')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'}
                      size="small"
                      color={job.list_type === 'whitelist' ? 'success' : 'error'}
                      variant="outlined"
                      icon={job.list_type === 'whitelist' ? <CheckCircleIcon /> : <BlockIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      {job.target_campaigns && job.target_campaigns.length > 0 ? (
                        <Box>
                          <Typography variant="body2" color="text.primary">
                            {job.target_campaigns.length} campaign(s)
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {job.target_campaigns.slice(0, 2).map((campaign) => (
                              <Chip
                                key={campaign.id}
                                label={campaign.name}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ))}
                            {job.target_campaigns.length > 2 && (
                              <Chip
                                label={`+${job.target_campaigns.length - 2} more`}
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No campaigns
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        icon={getStatusIcon(job.status)}
                        label={job.status}
                        color={getStatusColor(job.status)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 100 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={job.progress_percentage || 0}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography variant="body2" color="text.secondary">
                            {job.progress_percentage || 0}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" color="text.primary">
                        {job.processed_records || 0} / {job.total_records || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.successful_records || 0} successful
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.primary">
                      {formatDate(job.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => viewJobDetails(job)}
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Assign Campaigns">
                          <IconButton
                            size="small"
                            onClick={() => openCampaignAssignment(job)}
                            color="secondary"
                          >
                            <CampaignIcon />
                          </IconButton>
                        </Tooltip>
                        {job.status === 'pending' && (
                          <Tooltip title="Start Processing">
                            <IconButton
                              size="small"
                              onClick={() => handleJobAction(job.id, 'start')}
                              color="primary"
                            >
                              <PlayArrow />
                            </IconButton>
                          </Tooltip>
                        )}
                        {job.status === 'processing' && (
                          <Tooltip title="Cancel Job">
                            <IconButton
                              size="small"
                              onClick={() => handleJobAction(job.id, 'cancel')}
                              color="warning"
                            >
                              <Stop />
                            </IconButton>
                          </Tooltip>
                        )}
                        {job.status === 'failed' && (
                          <Tooltip title="Retry Job">
                            <IconButton
                              size="small"
                              onClick={() => handleJobAction(job.id, 'retry')}
                              color="primary"
                            >
                              <Refresh />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderJobDetails = () => (
    <Dialog
      open={showJobDetails}
      onClose={() => setShowJobDetails(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" color="text.primary">
          Job Details: #{selectedJob?.id}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {jobDetails ? (
          <Box>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="Overview" />
              <Tab label="Campaigns" />
              <Tab label="Progress" />
              <Tab label="Errors" />
            </Tabs>

            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="text.primary" gutterBottom>
                        Job Information
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Job Name"
                            secondary={jobDetails.name}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Import Type"
                            secondary={jobDetails.import_type}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="List Type"
                            secondary={jobDetails.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Status"
                            secondary={jobDetails.status}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Created"
                            secondary={formatDate(jobDetails.created_at)}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        {jobDetails.description && (
                          <ListItem>
                            <ListItemText
                              primary="Description"
                              secondary={jobDetails.description}
                              primaryTypographyProps={{ color: 'text.primary' }}
                              secondaryTypographyProps={{ color: 'text.secondary' }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="text.primary" gutterBottom>
                        Processing Statistics
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText
                            primary="Total Records"
                            secondary={jobDetails.total_records?.toLocaleString()}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Processed Records"
                            secondary={jobDetails.processed_records?.toLocaleString()}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Successful Records"
                            secondary={jobDetails.successful_records?.toLocaleString()}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary="Failed Records"
                            secondary={jobDetails.failed_records?.toLocaleString()}
                            primaryTypographyProps={{ color: 'text.primary' }}
                            secondaryTypographyProps={{ color: 'text.secondary' }}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  Campaign Information
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="text.primary" gutterBottom>
                          Target Campaigns
                        </Typography>
                        {jobDetails.target_campaigns && jobDetails.target_campaigns.length > 0 ? (
                          <List dense>
                            {jobDetails.target_campaigns.map((campaign) => (
                              <ListItem key={campaign.id}>
                                <ListItemIcon>
                                  <CampaignIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={campaign.name}
                                  secondary={`Status: ${campaign.status}`}
                                  primaryTypographyProps={{ color: 'text.primary' }}
                                  secondaryTypographyProps={{ color: 'text.secondary' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No target campaigns specified
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="text.primary" gutterBottom>
                          Assigned Campaigns
                        </Typography>
                        {jobDetails.assigned_campaigns && jobDetails.assigned_campaigns.length > 0 ? (
                          <List dense>
                            {jobDetails.assigned_campaigns.map((campaign) => (
                              <ListItem key={campaign.id}>
                                <ListItemIcon>
                                  <CheckCircleIcon color="success" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={campaign.name}
                                  secondary={`Applied: ${formatDate(campaign.applied_at)}`}
                                  primaryTypographyProps={{ color: 'text.primary' }}
                                  secondaryTypographyProps={{ color: 'text.secondary' }}
                                />
                                <Chip
                                  label={campaign.is_active ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={campaign.is_active ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No campaigns assigned yet
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>List Type:</strong> {jobDetails.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Auto-assign:</strong> {jobDetails.auto_assign_campaigns ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Campaign-specific import:</strong> {jobDetails.campaign_specific_import ? 'Yes' : 'No'}
                  </Typography>
                </Box>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  Processing Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Progress: {jobDetails.progress_percentage || 0}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={jobDetails.progress_percentage || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                {jobDetails.target_suppression_list && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Target Suppression List:</strong> {jobDetails.target_suppression_list.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {jobDetails.target_suppression_list.description}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  Processing Errors
                </Typography>
                {jobDetails.failed_records > 0 ? (
                  <Alert severity="warning">
                    <Typography variant="body2" color="text.primary">
                      {jobDetails.failed_records} records failed to process
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check the job logs for detailed error information
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="success">
                    <Typography variant="body2" color="text.primary">
                      No processing errors found
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <CircularProgress />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowJobDetails(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" color="text.primary">
          Bulk Import Jobs
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => navigate('/lead-gen/suppression/bulk-upload')}
          >
            New Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => {
              loadJobs();
              loadJobSummary();
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {renderJobSummary()}
      {renderJobsTable()}
      {renderJobDetails()}

      {/* Campaign Assignment Dialog */}
      <Dialog
        open={showCampaignAssignment}
        onClose={() => setShowCampaignAssignment(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="text.primary">
            Assign Campaigns to Job #{selectedJobForAssignment?.id}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedJobForAssignment && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select campaigns to assign the suppression list from this job to:
              </Typography>
              
              <CampaignSelector
                accountId={accountId}
                selectedCampaigns={selectedCampaignsForAssignment}
                onChange={setSelectedCampaignsForAssignment}
                helperText="Choose campaigns to assign the suppression list to"
              />
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.primary">
                  <strong>Note:</strong> This will create SuppressionListCampaign relationships for the suppression list created by this job.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCampaignAssignment(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCampaignAssignment}
            variant="contained"
            disabled={selectedCampaignsForAssignment.length === 0}
          >
            Assign Campaigns
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkImportJobs; 